import { useState } from 'react';
import { COUNTRIES } from '../data/content';
import { MAP } from '../constants/testIds';

// Approximate geographic coordinates -> SVG projection on a 1000x500 equirectangular canvas.
// Longitude (-180..180) -> x (0..1000); Latitude (90..-90) -> y (0..500)
const project = (lng, lat) => ({
  x: ((lng + 180) / 360) * 1000,
  y: ((90 - lat) / 180) * 500,
});

const COORDS = {
  IN: [78.96, 22.59],
  AE: [54.37, 24.46],
  SA: [45.08, 23.89],
  OM: [55.92, 21.47],
  GB: [-2.0, 54.5],
  BJ: [2.32, 9.30],
  MZ: [35.52, -18.66],
  TZ: [34.89, -6.37],
  ET: [40.49, 9.15],
};

const points = COUNTRIES.map((c) => {
  const [lng, lat] = COORDS[c.code] || [0, 0];
  return { ...c, coords: project(lng, lat) };
});

// Simple stylized continent silhouettes — minimalist, abstract (not geo-accurate)
const continents = [
  // North America
  'M150 90 L260 70 L320 110 L320 180 L280 210 L220 220 L170 200 L140 150 Z',
  // South America
  'M270 240 L310 230 L330 280 L320 360 L290 400 L270 390 L260 320 Z',
  // Europe
  'M460 95 L540 80 L580 110 L560 160 L500 170 L470 140 Z',
  // Africa
  'M490 175 L580 165 L600 240 L580 320 L540 360 L500 350 L480 280 L475 220 Z',
  // Middle East
  'M590 145 L640 140 L660 180 L630 200 L600 195 Z',
  // South Asia / India
  'M680 175 L720 170 L735 210 L720 240 L695 250 L678 220 Z',
  // East Asia
  'M740 140 L820 130 L850 180 L820 210 L780 215 L750 190 Z',
  // SE Asia / Australia
  'M820 280 L900 280 L920 330 L880 360 L830 340 Z',
];

export default function WorldMap({ compact = false }) {
  const [hovered, setHovered] = useState(null);

  return (
    <div
      data-testid={MAP.container}
      className="relative w-full"
      style={{ aspectRatio: '2 / 1' }}
    >
      <svg
        viewBox="0 0 1000 500"
        className="w-full h-full"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <pattern id="dots" x="0" y="0" width="6" height="6" patternUnits="userSpaceOnUse">
            <circle cx="1" cy="1" r="0.7" fill="rgba(13,27,42,0.18)" />
          </pattern>
          <radialGradient id="glow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#D4AF37" stopOpacity="0.7" />
            <stop offset="100%" stopColor="#D4AF37" stopOpacity="0" />
          </radialGradient>
        </defs>

        <rect width="1000" height="500" fill="url(#dots)" />

        {continents.map((d, i) => (
          <path
            key={i}
            d={d}
            fill="#0D1B2A"
            opacity="0.08"
            stroke="#0D1B2A"
            strokeOpacity="0.18"
            strokeWidth="0.6"
          />
        ))}

        {/* Highlight markers */}
        {points.map((p) => (
          <g
            key={p.code}
            data-testid={MAP.country(p.code)}
            onMouseEnter={() => setHovered(p)}
            onMouseLeave={() => setHovered(null)}
            style={{ cursor: 'pointer' }}
          >
            <circle cx={p.coords.x} cy={p.coords.y} r="22" fill="url(#glow)" pointerEvents="none" />
            <circle
              cx={p.coords.x}
              cy={p.coords.y}
              r="7"
              fill="#D4AF37"
              stroke="#0D1B2A"
              strokeWidth="1.5"
            />
            <circle
              cx={p.coords.x}
              cy={p.coords.y}
              r="7"
              fill="none"
              stroke="#D4AF37"
              strokeWidth="1.5"
              className="map-pulse-circle"
              opacity="0.6"
              pointerEvents="none"
            >
              <animate
                attributeName="r"
                from="7"
                to="22"
                dur="2.2s"
                repeatCount="indefinite"
              />
              <animate
                attributeName="opacity"
                from="0.6"
                to="0"
                dur="2.2s"
                repeatCount="indefinite"
              />
            </circle>
            {!compact && (
              <text
                x={p.coords.x + 12}
                y={p.coords.y - 8}
                fill="#0D1B2A"
                fontSize="11"
                fontFamily="Manrope, sans-serif"
                fontWeight="700"
              >
                {p.flag} {p.name}
              </text>
            )}
          </g>
        ))}
      </svg>

      {hovered && (
        <div
          data-testid={MAP.tooltip}
          className="absolute top-3 right-3 md:top-6 md:right-6 bg-white border border-[#0D1B2A]/10 shadow-xl p-5 max-w-xs"
        >
          <div className="text-2xl">{hovered.flag}</div>
          <div className="mt-1 font-display font-bold text-[#0D1B2A] text-lg">
            {hovered.name}
          </div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] mt-1">
            {hovered.role}
          </div>
          <div className="mt-3 flex items-baseline gap-2">
            <span className="font-stat font-extrabold text-3xl text-[#0D1B2A]">
              {hovered.projects}
            </span>
            <span className="text-xs uppercase tracking-[0.2em] text-[#4B5563]">
              Projects
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
